import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CMP-02 and CMP-04 at v1.967, compliance part 3: SWMS and risk assessments — written as a
// table on the 5 × 5 matrix, published in versions, linked to roles, venues and jobs, and signed by
// crew or recorded by the office. Flagged, never refused.

export type SafetySummary = components['schemas']['SafetyDocumentSummaryView'];
export type SafetyDocument = components['schemas']['SafetyDocumentView'];
export type SafetyRow = components['schemas']['SafetyRowView'];
export type SafetyGap = components['schemas']['SafetyGapView'];
export type SafetySources = components['schemas']['SafetySourcesView'];
export type Signoff = components['schemas']['SignoffView'];
export type CrewSafetyDocument = components['schemas']['CrewSafetyDocumentView'];
export type RiskBand = SafetyRow['bandBefore'];
export interface RowInput {
  step: string;
  hazards: string;
  likelihoodBefore: number;
  consequenceBefore: number;
  controls: string;
  likelihoodAfter: number;
  consequenceAfter: number;
  responsible: string | null;
}

export const BAND_LABEL: Record<RiskBand, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  extreme: 'Extreme',
};

/** The domain's 5 × 5 bands, mirrored for the editor's preview; the server's is the record. */
export function bandOf(likelihood: number, consequence: number): RiskBand {
  const score = likelihood * consequence;
  if (score <= 4) return 'low';
  if (score <= 9) return 'medium';
  if (score <= 14) return 'high';
  return 'extreme';
}

export const LIKELIHOOD = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost certain'] as const;
export const CONSEQUENCE = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'] as const;

/** "Rigging SWMS v3": what someone has to sign, in a line. */
export function toSignLabel(gaps: readonly { title: string; versionNumber: number }[]): string {
  return gaps.map((g) => `${g.title} v${g.versionNumber}`).join(', ');
}

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const swmsQuery = queryOptions({
  queryKey: ['safety', 'swms'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/swms');
    if (error) throw problem(error, 'Could not load the SWMS.');
    return data;
  },
});

export const safetyDocumentQuery = (id: string) =>
  queryOptions({
    queryKey: ['safety', 'document', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/safety-documents/{id}', {
        params: { path: { id } },
      });
      if (error) throw problem(error, 'Could not load that document.');
      return data;
    },
  });

export const signoffsQuery = (id: string) =>
  queryOptions({
    queryKey: ['safety', 'signoffs', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/safety-documents/{id}/signoffs', {
        params: { path: { id } },
      });
      if (error) throw problem(error, 'Could not load who has signed.');
      return data;
    },
  });

export const jobSafetyQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['safety', 'job', jobId],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}/safety', {
        params: { path: { id: jobId } },
      });
      if (error) throw problem(error, 'Could not load this job’s safety paperwork.');
      return data;
    },
  });

export const sourcesQuery = queryOptions({
  queryKey: ['safety', 'sources'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/safety-settings');
    if (error) throw problem(error, 'Could not load the setting.');
    return data;
  },
});

export async function createSafetyDocument(
  body: { kind: 'swms'; title: string } | { kind: 'risk_assessment'; title: string; jobId: string },
) {
  const { data, error } = await api.POST('/v1/safety-documents', { body });
  if (error) throw problem(error, 'Could not start that document.');
  return data;
}

export async function renameSafetyDocument(id: string, title: string) {
  const { error } = await api.PATCH('/v1/safety-documents/{id}', {
    params: { path: { id } },
    body: { title },
  });
  if (error) throw problem(error, 'Could not rename it.');
}

export async function saveDraft(id: string, rows: RowInput[]) {
  const { error } = await api.PUT('/v1/safety-documents/{id}/draft', {
    params: { path: { id } },
    body: { rows },
  });
  if (error) throw problem(error, 'Could not save the draft.');
}

export async function publish(id: string, needsResigning: boolean) {
  const { error } = await api.POST('/v1/safety-documents/{id}/publish', {
    params: { path: { id } },
    body: { needsResigning },
  });
  if (error) throw problem(error, 'Could not publish it.');
}

export async function retireSafetyDocument(id: string) {
  const { error } = await api.POST('/v1/safety-documents/{id}/retire', {
    params: { path: { id } },
  });
  if (error) throw problem(error, 'Could not retire it.');
}

export async function setLinks(id: string, roleIds: string[], venueIds: string[]) {
  const { error } = await api.PUT('/v1/safety-documents/{id}/links', {
    params: { path: { id } },
    body: { roleIds, venueIds },
  });
  if (error) throw problem(error, 'Could not save where it applies.');
}

export async function setJobSwms(jobId: string, swmsIds: string[]) {
  const { data, error } = await api.PUT('/v1/jobs/{id}/swms', {
    params: { path: { id: jobId } },
    body: { swmsIds },
  });
  if (error) throw problem(error, 'Could not save this job’s SWMS.');
  return data;
}

export async function setSources(body: SafetySources) {
  const { data, error } = await api.PUT('/v1/safety-settings', { body });
  if (error) throw problem(error, 'Could not save the setting.');
  return data;
}

export async function recordSignoff(id: string, crewMemberId: string, note: string) {
  const { error } = await api.POST('/v1/safety-documents/{id}/signoffs', {
    params: { path: { id } },
    body: { crewMemberId, note },
  });
  if (error) throw problem(error, 'Could not record that sign-off.');
}

export const crewSafetyDocumentQuery = (shiftId: string, documentId: string) =>
  queryOptions({
    queryKey: ['my-shifts', 'safety', shiftId, documentId],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/my/shifts/{id}/safety/{documentId}', {
        params: { path: { id: shiftId, documentId } },
      });
      if (error) throw problem(error, 'Could not open it.');
      return data;
    },
  });

export async function signMine(shiftId: string, documentId: string) {
  const { error } = await api.POST('/v1/my/shifts/{id}/safety/{documentId}/sign', {
    params: { path: { id: shiftId, documentId } },
  });
  if (error) throw problem(error, 'Could not sign it.');
}
